import * as SelectPrimitive from '@radix-ui/react-select';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as ToastPrimitive from '@radix-ui/react-toast';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/format.ts';

/**
 * The primitives, styled once.
 *
 * Radix rather than a component kit: the design in `design/` is specific enough
 * that a themed library would be fought at every step, but the parts genuinely
 * worth not writing by hand — focus traps, typeahead, escape handling, the
 * ARIA — are exactly what Radix is. Nothing here is a design decision; the
 * design decisions are the classes.
 */

/* ── buttons ────────────────────────────────────────────────────────────── */

export type ButtonTone = 'primary' | 'ghost' | 'danger' | 'card' | 'quiet';

const TONES: Record<ButtonTone, string> = {
  primary: 'bg-accent text-ink font-[650] hover:brightness-110 active:brightness-95',
  ghost: 'bg-btn border border-btnbd text-t2 hover:bg-btnhover hover:text-hi2',
  danger:
    'bg-transparent border border-warnx/30 text-warnx hover:bg-warnx/10 hover:border-warnx/50',
  // On the pale card, where the dark-zone tones would disappear.
  card: 'bg-cardbtn border border-cardbtnbd text-cardbtntx hover:bg-cardbtnhover hover:text-cardhi',
  quiet: 'bg-transparent text-mute hover:text-hi hover:bg-white/5',
};

export function Button({
  tone = 'ghost',
  size = 'md',
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: ButtonTone; size?: 'sm' | 'md' }) {
  return (
    <button
      {...props}
      className={cn(
        'rg-focus inline-flex items-center justify-center gap-1.5 rounded-full font-semibold whitespace-nowrap',
        'cursor-pointer transition-[background,color,filter] duration-100',
        'disabled:cursor-not-allowed disabled:opacity-45',
        size === 'sm' ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-[13px]',
        TONES[tone],
        className,
      )}
    />
  );
}

/* ── select ─────────────────────────────────────────────────────────────── */

export interface Option {
  value: string;
  label: string;
  hint?: string;
}

export function Select({
  value,
  onChange,
  options,
  size = 'md',
  className,
  placeholder,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  size?: 'sm' | 'md';
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
}) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onChange}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          'rg-focus inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-inputbd bg-input',
          'font-semibold text-t2 hover:border-btnbd data-[placeholder]:text-faint',
          size === 'sm' ? 'px-2 py-1 text-[11.5px]' : 'px-3 py-2 text-[13px]',
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon className="text-mute text-[8px] leading-none">▾</SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className="rg-anim z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border border-inputbd bg-side shadow-[0_18px_44px_rgba(10,12,24,0.5)]"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className="rg-focus flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] text-t2 outline-none select-none data-[highlighted]:bg-navhover data-[highlighted]:text-hi3 data-[state=checked]:text-accent"
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                {option.hint ? (
                  <span className="ml-auto pl-3 text-[11px] text-mute">{option.hint}</span>
                ) : null}
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/* ── kebab menu ─────────────────────────────────────────────────────────── */

/**
 * The overflow menu for a page that already has one or two primary buttons —
 * a "⋮" trigger, styled like the ✕ close button it usually sits next to, for
 * the actions that don't earn a button of their own.
 */
export function KebabMenu({
  label = 'More actions',
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          className="rg-focus grid size-7 shrink-0 place-items-center rounded-full text-soft hover:bg-btn hover:text-hi2"
        >
          ⋮
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={6}
          className="rg-anim z-50 min-w-[190px] overflow-hidden rounded-xl border border-line bg-side p-1 shadow-[0_18px_44px_rgba(10,12,24,0.5)]"
        >
          {children}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

export function MenuItem({
  children,
  onSelect,
  disabled,
}: {
  children: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.Item
      disabled={disabled}
      onSelect={onSelect}
      className="rg-focus flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] text-t2 outline-none select-none data-[highlighted]:bg-navhover data-[highlighted]:text-hi3 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-45"
    >
      {children}
    </DropdownMenuPrimitive.Item>
  );
}

/* ── checkbox ───────────────────────────────────────────────────────────── */

export function Checkbox({
  checked,
  onChange,
  label,
  id,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  id: string;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-[13px] text-t7">
      <CheckboxPrimitive.Root
        id={id}
        checked={checked}
        onCheckedChange={(next) => onChange(next === true)}
        className="rg-focus grid size-[15px] place-items-center rounded border border-btnbd bg-input data-[state=checked]:border-accent data-[state=checked]:bg-accent"
      >
        <CheckboxPrimitive.Indicator className="text-[10px] leading-none font-bold text-ink">
          ✓
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label}
    </label>
  );
}

/* ── inputs ─────────────────────────────────────────────────────────────── */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11.5px] font-semibold text-mute">{label}</span>
      {children}
      {hint ? <span className="text-[11.5px] leading-relaxed text-faint">{hint}</span> : null}
    </label>
  );
}

// `text-base` (16px), not the desktop `13px`, below `sm`: iOS Safari zooms the
// whole page in on focus for any input under 16px, and there is no way to
// suppress that from the page side short of the size itself being 16px+.
export const inputClass =
  'rg-focus w-full rounded-[10px] border border-inputbd bg-input px-3 py-2 text-base text-hi outline-none placeholder:text-faint sm:text-[13px]';

export function Input(props: React.ComponentPropsWithRef<'input'>) {
  return <input {...props} className={cn(inputClass, props.className)} />;
}

export function Textarea(props: React.ComponentPropsWithRef<'textarea'>) {
  return (
    <textarea {...props} className={cn(inputClass, 'resize-y leading-relaxed', props.className)} />
  );
}

/* ── dialog ─────────────────────────────────────────────────────────────── */

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-[#141828]/70 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className={cn(
            'rg-anim fixed top-1/2 left-1/2 z-50 max-h-[85vh] w-[92vw] -translate-x-1/2 -translate-y-1/2',
            'overflow-y-auto rounded-2xl border border-line bg-aside p-6 shadow-[0_24px_70px_rgba(10,12,24,0.55)]',
            wide ? 'max-w-3xl' : 'max-w-lg',
          )}
        >
          <DialogPrimitive.Title className="text-[17px] font-[650] tracking-[-0.01em] text-hi">
            {title}
          </DialogPrimitive.Title>
          {description ? (
            <DialogPrimitive.Description className="mt-1.5 text-[13px] leading-relaxed text-body">
              {description}
            </DialogPrimitive.Description>
          ) : null}
          <div className="mt-5 flex flex-col gap-4">{children}</div>
          {footer ? <div className="mt-6 flex items-center justify-end gap-2">{footer}</div> : null}
          <DialogPrimitive.Close
            aria-label="Close"
            className="rg-focus absolute top-4 right-4 grid size-7 place-items-center rounded-full text-soft hover:bg-btn hover:text-hi2"
          >
            ✕
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* ── tooltip ────────────────────────────────────────────────────────────── */

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={220} skipDelayDuration={300}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

export function Tip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
}) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="rg-anim z-50 max-w-xs rounded-lg border border-line bg-side px-2.5 py-1.5 text-[12px] leading-relaxed text-t2 shadow-[0_10px_30px_rgba(10,12,24,0.5)]"
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-side" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/* ── toasts ─────────────────────────────────────────────────────────────── */

interface Toast {
  id: number;
  title: string;
  body?: string;
  tone: 'ok' | 'bad';
}

const ToastContext = createContext<{
  notify: (title: string, options?: { body?: string; tone?: 'ok' | 'bad' }) => void;
} | null>(null);

/** Errors have to land somewhere a person will see them. This is that place. */
export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <Toaster>');
  return value;
}

export function Toaster({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const notify = useCallback(
    (title: string, options?: { body?: string; tone?: 'ok' | 'bad' }) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, title, body: options?.body, tone: options?.tone ?? 'ok' }]);
    },
    [],
  );

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={6000}>
        {children}
        {toasts.map((toast) => (
          <ToastPrimitive.Root
            key={toast.id}
            onOpenChange={(open) => {
              if (!open) setToasts((current) => current.filter((t) => t.id !== toast.id));
            }}
            className={cn(
              'rg-anim flex flex-col gap-1 rounded-xl border p-3.5 shadow-[0_18px_44px_rgba(10,12,24,0.5)]',
              toast.tone === 'bad'
                ? 'border-warnx/35 bg-[#3a2b31]'
                : 'border-line bg-side',
            )}
          >
            <ToastPrimitive.Title
              className={cn(
                'text-[13px] font-[650]',
                toast.tone === 'bad' ? 'text-warnx' : 'text-hi',
              )}
            >
              {toast.title}
            </ToastPrimitive.Title>
            {toast.body ? (
              <ToastPrimitive.Description className="text-[12.5px] leading-relaxed text-body">
                {toast.body}
              </ToastPrimitive.Description>
            ) : null}
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed right-5 bottom-5 z-[60] flex w-[360px] max-w-[92vw] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

/* ── small shared bits ──────────────────────────────────────────────────── */

export function Pill({
  tone,
  children,
  className,
}: {
  tone: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold whitespace-nowrap',
        tone,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('text-[12px] font-[650] text-mute', className)}>{children}</div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-5 py-16 text-center">
      <div className="text-[14.5px] font-semibold text-t5">{title}</div>
      {hint ? <div className="mt-1.5 text-[13px] text-mute">{hint}</div> : null}
    </div>
  );
}

/** A one-line skeleton, used only for the first paint. Everything after that is live. */
export function Shimmer({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-white/5', className)} />;
}
